import React, { Fragment, useState } from 'react';
import { Mutation } from 'react-apollo';
import { Link as LinkTo, useHistory } from 'react-router-dom';
import gql from 'graphql-tag';
import Linkify from 'react-linkify';
import parse from 'html-react-parser';
import {
  timeDifferenceForDate,
  commentFormatter,
  handleUnauthorized,
} from '../utils';
import { AUTH_TOKEN, USER_ID } from '../constants';
import Reply from './Reply';
import { LINK_QUERY } from './LinkDetails';
import { USER_COMMENTS_QUERY } from './Account';

const DELETE_COMMENT = gql`
  mutation deleteComment($commentId: ID!) {
    uncomment(commentId: $commentId) {
      id
    }
  }
`;

const VOTE_MUTATION = gql`
  mutation VoteMutation($commentId: ID!) {
    commentVote(commentId: $commentId) {
      id
      link {
        id
      }
      comment {
        id
      }
      user {
        id
      }
    }
  }
`;

const UNVOTE_MUTATION = gql`
  mutation UnVoteMutation($commentId: ID!) {
    commentUnvote(commentId: $commentId) {
      id
      link {
        comments {
          id
        }
      }
    }
  }
`;

const userHasVoted = (votes) => {
  const userId = localStorage.getItem(USER_ID);

  return (
    votes &&
    !!votes.filter((vote) => {
      return vote.user && vote.user.id === userId;
    }).length
  );
};

const updateStoreAfterVote = (store, voteData, commentId, linkId) => {
  const data = store.readQuery({
    query: LINK_QUERY,
    variables: { linkId },
  });

  const mutationType = voteData.commentVote ? 'commentVote' : 'commentUnvote';
  if (mutationType === 'commentVote') {
    data.link.comments.map((comment) => {
      if (comment.id === commentId) {
        comment.votes.push(voteData[mutationType]);
        return comment;
      } else {
        return comment;
      }
    });
  } else if (mutationType === 'commentUnvote') {
    data.link.comments.map((comment) => {
      if (comment.id === commentId) {
        comment.votes = comment.votes.filter((vote) => {
          return vote.id !== voteData[mutationType].id;
        });
      }
      return comment;
    });
  }
  store.writeQuery({
    query: LINK_QUERY,
    data,
    variables: { linkId },
  });
};

const userCanVote = (userId) => {
  return localStorage.getItem(USER_ID) !== userId;
};

const renderCommentVote = (
  authToken,
  user,
  votes,
  id,
  linkId,
  isVoting,
  setIsVoting,
) => {
  if (!authToken || !user) {
    return (
      <LinkTo
        to="/login"
        className="gray f11 upvote no-underline absolute comment-vote"
      >
        <span>▲</span>
      </LinkTo>
    );
  } else if (
    authToken &&
    user &&
    userCanVote(user.id) &&
    !userHasVoted(votes)
  ) {
    const history = useHistory();
    const checkUnauthorized = (error) => {
      handleUnauthorized(error, history);
    };
    return (
      <Mutation
        mutation={VOTE_MUTATION}
        variables={{ commentId: id }}
        update={(store, { data }) => {
          setIsVoting(false);
          updateStoreAfterVote(store, data, id, linkId);
        }}
        onError={(err) => {
          checkUnauthorized(err);
          setIsVoting(false);
        }}
      >
        {(voteMutation) => (
          <span
            className="gray f11 upvote absolute comment-vote"
            onClick={() => {
              setIsVoting(true);
              voteMutation();
            }}
          >
            {isVoting ? '' : '▲'}
          </span>
        )}
      </Mutation>
    );
  } else {
    return '';
  }
};

function Comment({
  id,
  text,
  user,
  created,
  replies,
  linkId,
  votes,
  forAccountPage,
  parent,
}) {
  const authToken = localStorage.getItem(AUTH_TOKEN);
  const [isVoting, setIsVoting] = useState(false);

  return (
    <div key={id} className="pt2">
      <div className="f8pt lh-copy gray pb1">
        {!forAccountPage &&
          (user ? (
            <LinkTo
              to={`/account/${user.id}`}
              className="no-underline gray link"
            >
              by {user.name} |
            </LinkTo>
          ) : (
            <span className="gray">by Unknown |</span>
          ))}
        <span> {timeDifferenceForDate(created)}</span>
        {!forAccountPage && (
          <span>
            {' '}
            |{' '}
            <LinkTo to={`/comment/${id}`} className="no-underline gray link">
              reply
            </LinkTo>
          </span>
        )}
        {user && user.id === localStorage.getItem(USER_ID) && (
          <Fragment>
            <span> | </span>
            <LinkTo
              to={`/editcomment/${id}`}
              className="no-underline gray link"
            >
              edit
            </LinkTo>
          </Fragment>
        )}
        {user &&
          user.id === localStorage.getItem(USER_ID) &&
          replies &&
          !replies.length && (
            <Mutation
              mutation={DELETE_COMMENT}
              variables={{ commentId: id }}
              update={(store, { data }) => {
                try {
                  const linkData = store.readQuery({
                    query: LINK_QUERY,
                    variables: { linkId },
                  });
                  linkData.link.comments = linkData.link.comments.filter(
                    (comment) => comment.id !== data.uncomment.id,
                  );
                  store.writeQuery({
                    query: LINK_QUERY,
                    data: linkData,
                    variables: { linkId },
                  });
                } catch (err) {
                  console.log('No link query in cache to update');
                }
                try {
                  const commentData = store.readQuery({
                    query: USER_COMMENTS_QUERY,
                    variables: { userId: user.id },
                  });

                  commentData.userComments.comments = commentData.userComments.comments.filter(
                    (comment) => comment.id !== data.uncomment.id,
                  );
                  store.writeQuery({
                    query: USER_COMMENTS_QUERY,
                    data: commentData,
                    variables: { userId: user.id },
                  });
                } catch (err) {
                  console.log('No user comments query in cache');
                }
                try {
                  // Invalidate the cached feed queries
                  Object.keys(store.data.data).forEach((key) => {
                    key.match(/ROOT_QUERY.feed/) && store.data.delete(key);
                  });
                } catch (err) {
                  console.log('No feed query in cache to invalidate');
                }
              }}
            >
              {(deleteComment) => (
                <Fragment>
                  <span> | </span>
                  <span className="gray link" onClick={deleteComment}>
                    (delete)
                  </span>
                </Fragment>
              )}
            </Mutation>
          )}
        {authToken && user && userCanVote(user.id) && userHasVoted(votes) && (
          <Mutation
            mutation={UNVOTE_MUTATION}
            variables={{ commentId: id }}
            update={(store, { data }) =>
              updateStoreAfterVote(store, data, id, linkId)
            }
          >
            {(voteMutation) => (
              <Fragment>
                <span> |</span>
                <span className="ml1 gray link" onClick={voteMutation}>
                  (unvote)
                </span>
              </Fragment>
            )}
          </Mutation>
        )}
        {forAccountPage && (
          <span className="ml1 gray">| on: {parent.description}</span>
        )}
      </div>

      <div className="f10pt fw2 lh-p respect-lb relative">
        {renderCommentVote(
          authToken,
          user,
          votes,
          id,
          linkId,
          isVoting,
          setIsVoting,
        )}
        <Linkify>{parse(commentFormatter(text))}</Linkify>
      </div>
      <div className="pl4 pt2">
        {!forAccountPage &&
          replies &&
          replies
            .sort((a, b) => b.votes.length - a.votes.length)
            .map((reply) => {
              return (
                <Reply
                  key={reply.id}
                  id={reply.id}
                  text={reply.text}
                  user={reply.user}
                  created={reply.created}
                  replies={reply.replies}
                  votes={reply.votes}
                  replyLevel={1}
                  linkId={linkId}
                />
              );
            })}
      </div>
    </div>
  );
}

export default Comment;



// WEBPACK FOOTER //
// ./src/components/Comment.js